import { useEffect, useRef, useState } from "react";
import { useTranslate } from "@/context/TranslateContext";
import { LANGUAGES, LANG_BY_CODE, speechLangFor } from "@/lib/languages";
import { Translate, SpeakerHigh, SpeakerSlash, Pause, Play, MagnifyingGlass, Check, CircleNotch } from "@phosphor-icons/react";

// Floating accessibility bar: whole-page language switcher + text-to-speech "Listen".
// Marked data-no-translate so its own labels stay readable in every language.
export default function AccessibilityBar() {
  const { lang, setLang, busy } = useTranslate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [speech, setSpeech] = useState("idle"); // idle | playing | paused
  const boxRef = useRef(null);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const current = LANG_BY_CODE[lang] || LANGUAGES[0];
  const isEmbed = typeof window !== "undefined" && window.location.pathname.startsWith("/embed/");

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Stop narration whenever the route/content changes or language switches.
  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  const filtered = LANGUAGES.filter((l) => {
    const s = q.trim().toLowerCase();
    return !s || l.label.toLowerCase().includes(s) || l.native.toLowerCase().includes(s) || l.code.includes(s);
  });

  const pick = async (code) => {
    setOpen(false); setQ("");
    if (supported) { window.speechSynthesis.cancel(); setSpeech("idle"); }
    if (code !== lang) await setLang(code);
  };

  const speak = () => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    const main = document.querySelector("main") || document.body;
    const text = (main.innerText || "").replace(/\s+/g, " ").trim().slice(0, 7000);
    if (!text) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const sl = speechLangFor(lang);
    u.lang = sl;
    const voices = synth.getVoices() || [];
    const v = voices.find((vv) => vv.lang && vv.lang.toLowerCase() === sl.toLowerCase())
      || voices.find((vv) => vv.lang && vv.lang.toLowerCase().startsWith(sl.slice(0, 2).toLowerCase()));
    if (v) u.voice = v;
    u.rate = 1; u.pitch = 1;
    u.onend = () => setSpeech("idle");
    u.onerror = () => setSpeech("idle");
    synth.speak(u);
    setSpeech("playing");
  };

  const toggleSpeak = () => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (speech === "playing") { synth.pause(); setSpeech("paused"); }
    else if (speech === "paused") { synth.resume(); setSpeech("playing"); }
    else speak();
  };

  const stopSpeak = () => { if (supported) { window.speechSynthesis.cancel(); setSpeech("idle"); } };

  if (isEmbed) return null;

  return (
    <div ref={boxRef} data-no-translate data-testid="accessibility-bar"
      className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2">
      {open && (
        <div className="w-72 overflow-hidden rounded-2xl border border-border bg-popover shadow-xl" data-testid="lang-menu">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <MagnifyingGlass size={15} className="text-muted-foreground" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search language…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" data-testid="lang-search" />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.map((l) => (
              <button key={l.code} onClick={() => pick(l.code)} data-testid={`lang-opt-${l.code}`}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${l.code === lang ? "text-secondary" : "text-foreground"}`}>
                <span><span className="font-medium">{l.native}</span> <span className="text-xs text-muted-foreground">{l.label}</span></span>
                {l.code === lang && <Check size={15} weight="bold" />}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-4 text-center text-sm text-muted-foreground">No match</p>}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 rounded-full border border-border bg-background/95 p-1 shadow-lg backdrop-blur">
        {supported && (
          <>
            <button onClick={toggleSpeak} data-testid="listen-toggle" title={speech === "playing" ? "Pause" : "Listen to this page"}
              className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent">
              {speech === "playing" ? <Pause size={17} weight="fill" className="text-secondary" />
                : speech === "paused" ? <Play size={17} weight="fill" className="text-secondary" />
                : <SpeakerHigh size={17} className="text-secondary" />}
              <span className="hidden sm:inline">{speech === "idle" ? "Listen" : speech === "playing" ? "Pause" : "Resume"}</span>
            </button>
            {speech !== "idle" && (
              <button onClick={stopSpeak} data-testid="listen-stop" title="Stop"
                className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <SpeakerSlash size={17} />
              </button>
            )}
            <span className="mx-0.5 h-5 w-px bg-border" />
          </>
        )}
        <button onClick={() => setOpen((v) => !v)} data-testid="lang-toggle" title="Change language"
          className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent">
          {busy ? <CircleNotch size={17} className="animate-spin text-secondary" /> : <Translate size={17} className="text-secondary" />}
          <span className="max-w-[90px] truncate">{current.native}</span>
        </button>
      </div>
    </div>
  );
}
